import Api, { executeAction } from 'src/helpers/api';
import { defaultApiVersionPath } from 'src/settings';
import { RATE_CARD_HTTP } from './constants';
import type { RateCardSideId } from './constants';

// `Api`'s own types demand every optional field on each call, which only bites here because these
// methods are called directly rather than handed to `sendRequest` as `(data: any) => Promise<any>`.
type ApiCall = (arg?: any) => Promise<any>;

interface IRateCardsApi {
  load: ApiCall;
  createItem: ApiCall;
  multipleParamsCreateItem: ApiCall;
  sendFormData: ApiCall;
}

// Annotated rather than cast, so a rename in the `Api` class fails the build, not at runtime.
const endpoint = (path: string): IRateCardsApi => new Api(path);

// `Api` interpolates a single positional `:id`, and only where `getPath` looks for it; a route whose
// id sits deeper, or that carries a second segment, is built here and issued through `executeAction`.
const path = (segments: string): string => `${defaultApiVersionPath}/pricing/${segments}`;

// `executeAction`'s inferred signature demands every key of its argument, so the two this module
// uses are named here rather than repeated as `undefined` at each call site.
const get = (url: string, search?: Record<string, unknown>): Promise<any> =>
  executeAction({ url, method: 'GET', search, data: undefined, params: undefined });

const destroy = (url: string): Promise<any> =>
  executeAction({ url, method: 'DELETE', search: undefined, data: undefined, params: undefined });

const formDataApi = endpoint('pricing/engine_versions/rate_cards/form_data');
const createApi = endpoint('pricing/engine_versions/:engine_version_id/rate_cards');
const cardApi = endpoint('pricing/rate_cards/:id');
const uploadsApi = endpoint('pricing/rate_cards/:id/uploads');
const publishApi = endpoint('pricing/rate_cards/:id/publish');

interface IIdArg {
  id: string;
}

interface ISearchArg {
  search?: Record<string, unknown>;
}

interface IRequestError extends Error {
  code?: number;
  /** The transport parks the bare validations map here, and only when the body carried one. */
  response?: Record<string, string[]>;
}

/** The reason phrases the transport splices in for the statuses this module recovers. */
const HTTP_REASONS: Record<number, string[]> = {
  [RATE_CARD_HTTP.CONFLICT]: ['Conflict'],
  [RATE_CARD_HTTP.UNPROCESSABLE]: ['Unprocessable Entity', 'Unprocessable Content'],
};

/**
 * The backend's own sentence, recovered by stripping the `"<status> <statusText> "` prefix the
 * transport prepends — it keeps no structured copy of the body.
 */
export const backendMessage = (error: IRequestError): string => {
  const status = error?.code;
  const message = error?.message ?? '';

  // Nothing recoverable: for a non-envelope body the transport builds the literal "undefined undefined"
  // while still setting `code`, and every caller prefers a truthy message over its own copy.
  if (!status || !message.startsWith(String(status))) return '';

  // The phrase is matched by name, not by shape: the backend's sentence also starts with a capital, so
  // "409 Conflict Try again" is indistinguishable from "409 Try again". Rack 3.1 renamed the 422 phrase.
  const phrases = HTTP_REASONS[status] ?? [];
  const withPhrase = new RegExp(`^${status}\\s+(?:${phrases.join('|')})\\s*`);

  if (phrases.length && withPhrase.test(message)) return message.replace(withPhrase, '');

  return message.replace(new RegExp(`^${status}\\s*`), '');
};

/**
 * Turns a 409 into a resolved `{ status, message }` rather than a rejection: the message names the
 * colliding columns, and `sendRequest` reads a `401` substring anywhere in it as a logout.
 */
const resolveConflict = (error: IRequestError) => {
  if (error?.code === RATE_CARD_HTTP.CONFLICT) {
    return { status: RATE_CARD_HTTP.CONFLICT, message: backendMessage(error) };
  }

  throw error;
};

// The transport assigns `code` for other 4xx but not for a 404, which arrives as `Error('404 Not Found')`.
const isNotFound = (error: IRequestError): boolean => error?.code === 404 || /^404\b/.test(error?.message ?? '');

export const loadFormData = () => formDataApi.load({});

// Every card of the organisation's country, across all engines and versions. The nested
// `engine_versions/:id/rate_cards` collection this replaced no longer answers a GET, so there is no fallback.
export const loadCards = ({ search }: ISearchArg) => get(path('rate_cards'), search);

/**
 * A 409 is recovered like the other rate-card calls': the version stopped being active or scheduled between
 * the form data being read and Continue being pressed, and the refusal's sentence is the whole payload.
 */
export const createCard = ({ data, params }: { data: unknown; params: { engine_version_id: string } }) =>
  createApi.multipleParamsCreateItem({ data, params }).catch(resolveConflict);

// Resolved, not rejected, so the detail page can tell a missing card from a failed request.
export const loadCard = ({ id }: IIdArg) =>
  cardApi.load({ id }).catch((error: IRequestError) => {
    if (isNotFound(error)) return { status: RATE_CARD_HTTP.NOT_FOUND };

    throw error;
  });

/**
 * Draft cards only. Two refusals are recovered rather than re-thrown: a 409 whose sentence is the whole
 * payload — nothing in the body separates "published" from "publish running" — and a 404, already gone.
 */
export const deleteCard = ({ id }: IIdArg) =>
  destroy(path(`rate_cards/${id}`)).catch((error: IRequestError) => {
    if (isNotFound(error)) return { status: RATE_CARD_HTTP.NOT_FOUND };

    return resolveConflict(error);
  });

export const downloadTemplate = ({ engineVersionId, side }: { engineVersionId: string; side: RateCardSideId }) =>
  get(path(`engine_versions/${engineVersionId}/rate_cards/${side}/template`)).catch(resolveConflict);

export const exportLines = ({ id, side }: IIdArg & { side: RateCardSideId }) =>
  get(path(`rate_cards/${id}/${side}/export`)).catch(resolveConflict);

export const uploadSide = ({ data, id }: { data: FormData; id: string }) => uploadsApi.sendFormData({ data, id });

// The refusal sentences are the whole payload of a failed publish, and `preserveErrorStatus` keeps
// `validations` while dropping `messages` — so the sentence is recovered for the two refusal statuses.
export const publishCard = ({ id }: IIdArg) =>
  publishApi.createItem({ data: {}, id }).catch((error: IRequestError) => {
    // 401, 403 and 500 must keep rejecting so `sendRequest`'s own handling still runs; resolving
    // them here would swallow a session expiry the way the upload's `ignoreLogout` used to.
    if (error?.code !== RATE_CARD_HTTP.CONFLICT && error?.code !== RATE_CARD_HTTP.UNPROCESSABLE) throw error;

    return { status: error.code, message: backendMessage(error), validations: error.response };
  });

/**
 * The pre-publish probe: the same validation publish runs, asked for up front. Read-only, so it is
 * safe to re-ask after a corrected file — but not free, which is why nothing polls it.
 */
export const loadValidation = ({ id }: IIdArg) => get(path(`rate_cards/${id}/validation`));

export const loadLines = ({ id, side, search }: IIdArg & { side: RateCardSideId } & ISearchArg) =>
  get(path(`rate_cards/${id}/lines/${side}`), search);

export const loadLineHeaders = ({ id, side }: IIdArg & { side: RateCardSideId }) =>
  get(path(`rate_cards/${id}/lines/${side}/headers`));
